import { useSession } from '../../../state/session';
import { HeaderCard } from '../../HeaderCard/HeaderCard';
import { renderTemplate } from '../../utils';
import { CmsMeta } from '../cmsMeta';

type CmsAccountHeaderProps = {
	heading: string;
	subheading: string;
	image: string;
	items?: string[];
};

/** a greeting stops reading as one somewhere around here, and a name field holds whatever was typed */
const LONGEST_NAME = 30;

/**
 * Tidies what a missing value leaves behind. A customer with no first name on the account would
 * otherwise be greeted with "Hej !", since the template has a space and an exclamation mark around
 * a token that resolved to nothing.
 */
function withoutTheGap(text: string): string {
	return text
		.replace(/\s+/g, ' ')
		.replace(/\s+([!?.,:;])/g, '$1')
		.trim();
}

export const CmsAccountHeader = ({ heading, subheading, image, items }: CmsAccountHeaderProps) => {
	const { isLoggedIn, user } = useSession();
	if (!isLoggedIn || !user) return <></>;

	const firstname =
		user.firstname.length > LONGEST_NAME ? `${user.firstname.slice(0, LONGEST_NAME)}...` : user.firstname;

	return (
		<HeaderCard
			heading={withoutTheGap(renderTemplate(heading, { firstname }))}
			subheading={subheading}
			listItems={items ?? []}
			bannerImage={image}
		/>
	);
};

export const cmsMeta: CmsMeta = {
	description:
		"The block at the top of My pages: a greeting and a few lines about the page beside a picture. Use {{firstname}} in the heading for the customer's own name - a customer with none is greeted without it rather than with a gap. `items` are the ticked lines under the text. Renders nothing at all when nobody is signed in, so never put anything essential in it.",
	example: [
		'CmsAccountHeader',
		{
			heading: 'Hej {{firstname}}!',
			subheading:
				'På Mina sidor kan du följa dina pågående leveranser och få överblick över dina kontoinställningar',
			image: '/images/my-pages-header-image.png',
			items: [
				'Följ statistik över dina köp över tid',
				'Sätt egna specialpriser & håll koll på din bonus',
				'Prenumerera på varor du köper ofta',
				'Hantera favoriter & tidigare köp',
			],
			index: 0,
		},
	],
};
